import React, { useState, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { compose } from 'recompose';
import { withTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

import { withConfig } from 'Context/ConfigContext';
import { Box, Flex, Text, SvgIcon, ButtonFilled } from 'Components/Base';
import { withUsername } from 'Redux/user-profile/hoc';
import { localeDate } from 'Util/datetime';
import BaseHTMLView from 'Components/HTMLView';
import { withAnnouncement } from 'HOC/Announcement';
import { PATHS } from 'Routes/Main/paths';

import { ANNOUNCEMENT_CATEGORY, COURSE_ANNOUNCEMENT_CATEGORIES, readAnnouncement } from '../utils';

const Container = styled(Flex)`
  ${({ isClickableTab }) =>
    css`
      box-shadow: 0px 1px ${({ theme }) => theme.colors.greyscale.grey4};
      margin-bottom: 1px;
      cursor: pointer;
      flex-shrink: 0;
      flex-basis: auto;
      align-items: stretch;
      cursor: ${isClickableTab ? 'pointer' : 'default'};
      &:active {
        background-color: ${({ theme }) => theme.colors.greyscale.grey3};
      }
    `}
`;

const HTMLView = styled(BaseHTMLView)`
  p {
    margin: 0 0 ${({ theme }) => `${theme.space[4]}px`};
    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const Badge = styled(Box)`
  display: inline-block;
  border-radius: 9999px;
`;

const CONTENT_TYPE = {
  HTML: 'HTML',
  MARKDOWN: 'MARKDOWN',
};

const NOTI_BADGE = {
  TIPS: 'TAB2',
  REMINDER: 'REMINDER',
};

const checkIsCourseAnnouncement = (category, courseTitle) => {
  return COURSE_ANNOUNCEMENT_CATEGORIES.includes(category) && courseTitle && courseTitle.length > 0;
};

const Content = ({
  appConfig: { uiVariant, notification },
  isNew,
  id,
  title,
  caption,
  courseTitle,
  rawContent,
  contentType,
  category,
  announcer,
  publishedTime,
  onNewItemClick,
  onItemClick,
  t,
  username,
  redirectUrl,
  onRedirectUrl,
  announcementContext: { markNotificationAsRead },
}) => {
  const [_new, setNew] = useState();
  const isClearCategory = category === ANNOUNCEMENT_CATEGORY.CLEARS;
  const isInternalOrExternalCategory = category === ANNOUNCEMENT_CATEGORY.INTERNAL_OR_EXTERNAL_URL;
  const isRedirectMyCourses = redirectUrl === PATHS.myCourses;
  // NOTE: To check notification news, click the enabled tab
  const isClickableTab = (!isClearCategory && !isInternalOrExternalCategory) || isRedirectMyCourses;

  useEffect(() => {
    setNew(isNew);
  }, [isNew]);

  const renderNotificationBadge = () => {
    if (_new) {
      return (
        <Box mb={3}>
          <Badge bg={'badge'} px={4} pt="1px">
            <Text textStyle={'body2'} color={'white'}>
              {t('NEW')}
            </Text>
          </Badge>
        </Box>
      );
    }
    return;
  };

  const renderTitleContent = () => {
    const isCourseAnnouncement = checkIsCourseAnnouncement(category, courseTitle);
    if (uiVariant === 'PAAS') {
      return (
        <Fragment>
          {!isCourseAnnouncement ? (
            <Box mb={3}>
              <Badge bg={'danger'} px={4} py={1}>
                <Text textStyle={'body2'} color={'white'}>
                  {category === 'NEWS' ? t('NEWS') : t(NOTI_BADGE[notification?.type ?? 'TIPS'])}
                </Text>
              </Badge>
            </Box>
          ) : null}
          {isCourseAnnouncement ? (
            <Text textStyle={'body2'} fontWeight={'normal'} color={'bsGrays.gray500'} mb={1}>
              {courseTitle}
            </Text>
          ) : null}
          <Text textStyle={'body1Medium'}>{title}</Text>
          <Box mt={1}>
            <Text textStyle={'body2'} fontWeight={'normal'} color={'bsGrays.gray500'}>
              {t('ANNOUNCED_BY', { name: announcer })}
              <Flex mt={2}>
                <Flex mr={3} alignItems={'center'}>
                  <SvgIcon icon={SvgIcon.ICON_LIST.CALENDAR_OUTLINED} mr={1} />
                  {localeDate(publishedTime, 'th', 'DD/MM/YYYY')}
                </Flex>
                <Flex alignItems={'center'}>
                  <SvgIcon icon={SvgIcon.ICON_LIST.CLOCK_OUTLINED} mr={1} />
                  {localeDate(publishedTime, 'th', 'HH:mm')}
                </Flex>
              </Flex>
            </Text>
          </Box>
        </Fragment>
      );
    }
    return (
      <Fragment>
        {renderNotificationBadge()}
        <Box>
          <Text textStyle={'heading5'}>{title}</Text>
        </Box>
        <Box mt={2}>
          <Text textStyle={'caption'} color={'notiGrey.grey1'}>
            {caption}
          </Text>
        </Box>
      </Fragment>
    );
  };

  const handleOnClick = () => {
    readAnnouncement(id, username);
    setNew(false);
    onNewItemClick && onNewItemClick();
  };

  const handleClick = () => {
    // NOTE: Previous notifications related to news
    if (!isClearCategory && !isInternalOrExternalCategory) {
      if (_new) {
        handleOnClick();
      } else {
        onItemClick();
      }
    }
    // NOTE: Recent notifications related to invitations or feature announcements
    else if (isRedirectMyCourses) {
      _new && markNotificationAsRead([id]);
      setNew(false);
    }
  };

  const renderBackgroundColor = () => {
    if (uiVariant === 'PAAS') {
      return _new ? 'primaryLighter' : 'white';
    }

    return _new ? 'notiGrey.grey2' : 'white';
  };

  const backgroundColor = renderBackgroundColor();

  return (
    <Container
      bg={backgroundColor}
      p={6}
      flexDirection={'column'}
      onClick={handleClick}
      isClickableTab={isClickableTab}
    >
      {renderTitleContent()}
      {rawContent && (
        <Box my={2}>
          {CONTENT_TYPE.HTML === contentType && <HTMLView rawContent={rawContent} />}
          {CONTENT_TYPE.MARKDOWN === contentType && <ReactMarkdown source={rawContent} />}
        </Box>
      )}
      {(isClearCategory || isInternalOrExternalCategory) && !isRedirectMyCourses && (
        <Box>
          <ButtonFilled
            height={'40px'}
            borderRadius={'20px'}
            py={0}
            onClick={(e) => {
              e.stopPropagation();
              if (onRedirectUrl) {
                _new && markNotificationAsRead([id]);
                setNew(false);
                onRedirectUrl(redirectUrl);
              }
            }}
          >
            {t('BUTTON.VIEW_MORE')}
          </ButtonFilled>
        </Box>
      )}
    </Container>
  );
};

Content.propTypes = {
  isNew: PropTypes.bool,
  id: PropTypes.string,
  title: PropTypes.string,
  caption: PropTypes.string,
  courseTitle: PropTypes.string,
  rawContent: PropTypes.string,
  contentType: PropTypes.string,
  category: PropTypes.string,
  announcer: PropTypes.string,
  publishedTime: PropTypes.string,
  onNewItemClick: PropTypes.func,
  onItemClick: PropTypes.func,
  appConfig: PropTypes.object,
  redirectUrl: PropTypes.string,
  onRedirectUrl: PropTypes.func,
};

Content.defaultProps = {
  isNew: false,
  onClick: () => {},
  onRedirectUrl: null,
};

export default compose(
  withTranslation('inbox'),
  withUsername,
  withAnnouncement,
  withConfig
)(Content);
